import { Loggable } from 'app/utils/logging/loggable';

import { strToHex } from '../utils/theme';

import {
  LANDSCAPE_MAP_HORIZONTAL_PADDING,
  LANDSCAPE_MAP_VERTICAL_PADDING,
  MOBILE_HEIGHT_THRESHOLD,
  MOBILE_PHONE_WIDTH_THRESHOLD,
  PORTRAIT_MAP_HORIZONTAL_MARGIN,
  PORTRAIT_MAP_HORIZONTAL_PADDING,
  PORTRAIT_MAP_VERTICAL_PADDING,
} from './mini-map-helper.constants';
import { MiniMapState } from './mini-map-state';
import { Point } from 'pixi.js';

const defaultWidth = 400;
const defaultHeight = 400;
const defaultZoomScale = 400;
export default class MiniMapHelper extends Loggable {
  public width = 0;
  public height = 0;
  public miniWorldTop = 0;
  public miniWorldLeft = 0;
  public backgroundColor = 0;
  public ratio = 0;

  public maxZoomOut = defaultZoomScale; // limit on zoom out
  private maxZoomIn = 160; // limit on zoom in
  public zoom = defaultZoomScale; // 100% zoom
  private zoomBy = 0.1; // zoom based on 10%

  public constructor(
    public state?: MiniMapState,
    public worldWidth: number = defaultWidth,
    public worldHeight: number = defaultHeight,
    public zoomScale: number = defaultZoomScale,
  ) {
    super();
    this.setDisplayName('MiniMapHelper');
    if (state) {
      this.setState(state);
    }

    this.maxZoomOut = zoomScale;
    this.zoom = this.maxZoomOut;
  }

  /**
   * sets map state and determines sizes based on state
   * @param state the state to set
   */
  public setState(state: MiniMapState): void {
    this.state = state;
    switch (state) {
      case MiniMapState.CLOSED:
      case MiniMapState.NONE:
        if (window.innerWidth < MOBILE_PHONE_WIDTH_THRESHOLD || window.innerHeight < MOBILE_HEIGHT_THRESHOLD) {
          this.updateMobileSize();
        } else {
          this.updateDesktopSize(this.worldWidth, 300);
        }
        break;
      case MiniMapState.OPENED:
        this.updateDesktopSize(this.worldWidth, 300);
        break;
      case MiniMapState.EXPANDED:
        this.updateDesktopSize(this.worldWidth, 400);
        break;
      case MiniMapState.MOBILE:
        this.updateMobileSize();
        break;
      default:
        break;
    }
  }

  /**
   * Basically, use margin and padding constants to set the height/width of the minimap container,
   * and, set the height/width of the map layer itself. Minimap layer will always honour the bigger size of
   * its parent, if the height of the parent is bigger than the width of the parent, minimap will
   * use the size of the parent's height to calculate the width and vice-versa.
   */
  private updateMobileSize(): void {
    const isPortrait = window.innerHeight > window.innerWidth;
    this.height = window.innerHeight / 2;
    if (isPortrait) {
      this.width = window.innerWidth - PORTRAIT_MAP_HORIZONTAL_MARGIN;
      this.worldWidth = this.width - PORTRAIT_MAP_HORIZONTAL_PADDING;
      this.worldHeight = this.worldWidth;
      if (this.worldHeight >= this.height) {
        this.worldHeight = this.height - PORTRAIT_MAP_VERTICAL_PADDING;
        this.worldWidth = this.worldHeight;
      }
    } else {
      this.width = window.innerWidth / 2;
      this.worldHeight = this.height - LANDSCAPE_MAP_VERTICAL_PADDING;
      this.worldWidth = this.worldHeight;
      if (this.worldWidth >= this.width) {
        this.worldWidth = this.width - LANDSCAPE_MAP_HORIZONTAL_PADDING;
        this.worldHeight = this.worldWidth;
      }
    }
  }

  /**
   * updates world and mini map values in desktop and large tablet devices
   * @param worldSize the size of the world
   * @param layerSize the size of the map container
   */
  private updateDesktopSize(worldSize: number, layerSize: number): void {
    this.worldWidth = worldSize;
    this.worldHeight = worldSize;
    this.miniWorldLeft = 0;
    this.miniWorldTop = 0;
    this.width = layerSize;
    this.height = layerSize;
  }

  /**
   * gets scale based on current ratio
   * @param size
   * @returns the current scale
   */
  public getScale(size: number): number {
    return size * this.ratio;
  }

  /**
   * used to calculate the zoom percentage
   * @param zoomIn - Whether the view is zoomed in or not
   * @returns current zoom
   */
  public getZoom(zoomIn: boolean): number {
    const zoom = this.width * this.zoomBy;
    if (zoomIn) {
      this.zoom -= zoom;
    } else {
      this.zoom += zoom;
    }
    return this.zoom;
  }

  /**
   * reset the zoom value for responsive changes
   */
  public resetZoom(): void {
    this.zoom = defaultZoomScale;
  }

  /**
   * used to calculate where the zoom should center in
   * @param zoomIn - Whether the view is zoomed in or not
   * @param x - X axis of the point
   * @param y - Y axis of the point
   * @returns a new Point
   */
  public getZoomCenter(zoomIn: boolean, x: number, y: number): Point {
    const center = new Point(x, y);
    return center;
  }

  /**
   * used to determine if either zoom in or zoom out has maxed out
   * @returns whether the view is zoomed max in/out
   */
  public isZoomMaxed(): boolean {
    return this.zoom === this.maxZoomOut || this.zoom === this.maxZoomIn;
  }

  /**
   *
   * @returns if zoom in has maxed out
   */
  public isZoomInMaxed(): boolean {
    return this.zoom <= this.maxZoomIn;
  }

  /**
   *
   * @returns if zoom out has maxed out
   */
  public isZoomOutMaxed(): boolean {
    return this.zoom >= this.maxZoomOut;
  }

  /**
   *
   * @returns if viewport center is allowed
   */
  public allowCenterShift(): boolean {
    return this.zoom < this.maxZoomOut;
  }

  /**
   * sets and tracks background color for mini map
   * @param color - the color to set
   */
  public setBackgroundColor(color: string): void {
    this.backgroundColor = strToHex(color);
  }

  public setZoom(value: number): void {
    this.zoom = value;
  }
}
